import * as React from 'react';
// Import material ui modules.
import {
	Check,
	Close,
} from '@mui/icons-material';
// Import hooks.
import {
    useInputDropdown,
    UseInputDropdownResults,
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import props.
import {
	FormPopupProps,
	NavigationFooterProps,
	FormPopupChangeProducerAgentProps,
	ScrollbarProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
	NavigationFooterAlignment,
	PopupWidth,
} from '@app/enumerations';
// Import app models.
import {
	DropdownItem,
} from '@app/models/app';
// Import api models.
import {
	PossibleProducerAgent
} from '@app/models/api';

interface UseFormPopupChangeProducerAgentParameters {
	id: string;
	props: FormPopupChangeProducerAgentProps,
}

interface UseFormPopupChangeProducerAgentResults {
	submitForm: () => void;
	producerAgentInput: UseInputDropdownResults;
	applyFormProps: () => FormPopupProps & ScrollbarProps;
	applyNavigationFooterProps: () => NavigationFooterProps;
}

const useFormPopupChangeProducerAgent = (params: UseFormPopupChangeProducerAgentParameters): UseFormPopupChangeProducerAgentResults => {

	// #region exported methods

	/**
	 *  Starts the submission process with validation for the address.
	 **/
	const submitForm = (): void => {
		// First flag all inputs to show any validation errors.
		flagAllInputsToShowValidations();
		// Validate the whole form.
		let isValid: boolean = checkFormValidationWithFocus();
		// If form passes validation, proceed to submission.
		if (isValid) {
			let selectedProducerAgentId: number | undefined = getSelectedProducerAgentId();
			if (selectedProducerAgentId !== undefined) {
				params.props.onSave(selectedProducerAgentId);
			}
		}
	}

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormPopupProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
			title: 'Change Producer Agent',
			themeType: AppThemeColor.PRIMARY,
			fixedNavigation: true,
			isOpen: params.props.isOpen,
			popupWidth: PopupWidth.MEDIUM,
			onCloseClick: params.props.onClose,
		};
	};

	/**
	 * Returns props for the navigation footer. 
	 **/
	const applyNavigationFooterProps = (): NavigationFooterProps => {
		return {
			id: generateIdForNavigationFooter(),
			leftButtonDisabled: false,
			leftButtonLabel: 'Close',
			leftButtonClick: params.props.onClose,
			leftButtonIcon: Close,
			rightButtonDisabled: false,
			rightButtonLabel: 'Save',
			rightButtonClick: submitForm,
			rightButtonIcon: Check,
			alignment: NavigationFooterAlignment.RIGHT,
		};
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Fetches the selected producer agent id. 
	 **/
	const getSelectedProducerAgentId = (): number | undefined => {
		let selectedProducerAgentId: number | undefined = undefined;
		if (producerAgentInput.value !== undefined) {
			selectedProducerAgentId = parseInt(producerAgentInput.value);
			selectedProducerAgentId = isNaN(selectedProducerAgentId) ? undefined : selectedProducerAgentId;
		}
		return selectedProducerAgentId;
	}

	/**
	 * Converts the list of possible producer agents to dropdown items. 
	 **/
	const convertProducerAgentsToDropdownItems = (): DropdownItem[] => {
		let producerAgentDropdownItems: DropdownItem[] = [];
		params.props.possibleProducerAgents.forEach((possibleProducerAgent: PossibleProducerAgent) => {
			if (possibleProducerAgent.id !== null) {
				producerAgentDropdownItems.push(
					new DropdownItem({
						label: possibleProducerAgent.displayName ?? 'Unknown',
						value: possibleProducerAgent.id.toString(),
					})
				);
			}
		});

		return producerAgentDropdownItems;
	}

	const getDefaultProducerAgentDropdownItem = (): DropdownItem => {
		return new DropdownItem({
			label: '--Select--',
			value: String.empty()
		});
	}

	/**
	 * Validates an individual input with focus.
	 * @param input
	 */
	const checkInputValidationWithFocus = <T>(input: UseInputFormResult<T>): boolean => {
		let isValid = input.isInputValid;
		if (!isValid) {
			input.focusToInput();
		}
		return isValid;
	}

	/**
	 * Method to check form validation with focus. 
	 **/
	const checkFormValidationWithFocus = (): boolean => {
		return checkInputValidationWithFocus<string>(producerAgentInput);
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		producerAgentInput.changeShowValidationFlag(true);
	}

	//#region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the navigation footer. 
	 **/
	const generateIdForNavigationFooter = (): string => {
		return `${generateIdForForm()}-navigationFooter`;
	}

	/**
	 * Generate id for producer agent. 
	 **/
	const generateIdForProducerAgent = (): string => {
		return `${generateIdForForm()}-producerAgent`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	// Instantiate the 'producer agent' input hook.
	const producerAgentInput: UseInputDropdownResults = useInputDropdown({
		id: generateIdForProducerAgent(),
		label: 'Producer Agent',
		required: true,
		disabled: false,
		value: params.props.selectedProducerAgentId?.toString() ?? String.empty(),
		dropdownItems: convertProducerAgentsToDropdownItems(),
		defaultDropdownItem: getDefaultProducerAgentDropdownItem(),
		showValidation: false,
	});

	// #endregion variables


	// #region life-cycle methods.

	// Called when the hook is initially mounted, and returns a call back that will be called on unmounting.
	React.useEffect(() => {
		// Initially the first input should be focused.
		producerAgentInput.focusToInput();
	}, []);

	// #endregion life-cycle methods.


	return {
		submitForm: submitForm,
		producerAgentInput: producerAgentInput,
		applyFormProps: applyFormProps,
		applyNavigationFooterProps: applyNavigationFooterProps,
	};
}

// Export parameters.
export {
	UseFormPopupChangeProducerAgentParameters,
}

// Export results.
export {
	UseFormPopupChangeProducerAgentResults,
}

export default useFormPopupChangeProducerAgent;